import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  type Issue,
  type Subprocessor,
  type SubprocessorWrite,
} from '../lib/api'
import { DataTable, ErrorBox, Loading, type Column } from './DataTable'
import { Status, Toggletip, type Tone } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import { checkNotAfterToday, todayIso } from '../lib/dateRules'
import '../styles/sweep.css'
import { ErrorReferenceOf } from './ui'

// The parties behind the vendor, by name.
//
// A sub-processor used to be one tick on the vendor record saying that
// sub-processors exist. That answers nothing an incident asks: which of our data
// reaches them, which country they are in, and which of our other vendors sit
// behind the same party. So each one is a row, and a row is withdrawn rather
// than deleted, because who stood behind this vendor last March is a question
// that gets asked after the arrangement has already changed.
//
// # THE NAME IS THE IDENTITY, SO THE FORM WILL NOT RENAME
//
// The register is keyed on (tenant, vendor, lower(legal_name)) and the update
// route does not accept a name for that reason: a rename through the same index
// would create a second row and leave the first standing, breaking both the
// chain length the tier rule counts and the traversal that asks which vendors
// sit behind one party. The field is therefore locked when editing rather than
// sent and quietly ignored, which is what the screen used to do.

const DECLARED_BY = [
  { value: 'VENDOR', label: 'The vendor declared it' },
  { value: 'TENANT', label: 'We found it ourselves' },
]

// detail is the one line under the status box; why is the reason behind it,
// behind the (i), for the statuses that have one worth reading.
const STATUSES: { value: string; label: string; tone: Tone; detail: string; why?: string }[] = [
  {
    value: 'DECLARED',
    label: 'Declared',
    tone: 'neutral',
    detail: 'Named, nothing checked yet',
    why: 'Everything the vendor tells us starts here, until somebody checks it.',
  },
  {
    value: 'VERIFIED',
    label: 'Verified',
    tone: 'ok',
    detail: 'Checked against the agreement, a list, or the vendor in writing',
  },
  {
    value: 'WITHDRAWN',
    label: 'Withdrawn',
    tone: 'plain',
    detail: 'Out of the chain, the row stays on record',
    why: 'The row stays so that an assessment taken while the party was in the chain still reads true.',
  },
]

const STATUS_LABEL: Record<string, string> = Object.fromEntries(STATUSES.map((s) => [s.value, s.label]))
const STATUS_TONE: Record<string, Tone> = Object.fromEntries(STATUSES.map((s) => [s.value, s.tone]))

// India as the API spells it, and the same reason as the data panel beside
// this one: the residency view compares against IN, so a friendlier reading
// here would disagree with it about the same party.
const INDIA = 'IN'

const ALPHA2 = /^[A-Z]{2}$/

function outsideIndia(country: string | undefined): boolean {
  const c = (country ?? '').trim().toUpperCase()
  return c !== '' && c !== INDIA
}

type Draft = {
  legalName: string
  country: string
  serviceDescription: string
  declaredBy: string
  firstSeenOn: string
  status: string
}

const EMPTY_DRAFT: Draft = {
  legalName: '',
  country: '',
  serviceDescription: '',
  declaredBy: 'VENDOR',
  firstSeenOn: '',
  status: 'DECLARED',
}

type Editing = { mode: 'new' } | { mode: 'edit'; row: Subprocessor }

function validate(draft: Draft, naming: boolean, storedSeen = ''): Issue[] {
  const issues: Issue[] = []
  if (naming && draft.legalName.trim() === '') {
    issues.push({
      field: 'legalName',
      code: 'REQUIRED',
      detail: 'Name the party as the agreement does, the register matches on it',
    })
  }
  if (draft.serviceDescription.trim() === '') {
    issues.push({
      field: 'serviceDescription',
      code: 'REQUIRED',
      detail: 'Say what they do with our data',
    })
  }
  const country = draft.country.trim().toUpperCase()
  if (country !== '' && !ALPHA2.test(country)) {
    issues.push({
      field: 'country',
      code: 'INVALID',
      detail: country + ' is not a two letter code (India is IN), leave it blank if not known',
    })
  }
  issues.push(...firstSeenIssues(draft, storedSeen))
  return issues
}

// D24 of the date rules: a party is seen before it is recorded. Said when the
// person leaves the field as well as on Save, and only for a date being moved:
// a stored date never blocks an edit of something else.
function firstSeenIssues(draft: Draft, storedSeen = ''): Issue[] {
  if (draft.firstSeenOn === storedSeen) return []
  const msg = checkNotAfterToday('First seen on', draft.firstSeenOn, todayIso())
  return msg ? [{ field: 'firstSeenOn', code: 'IN_THE_FUTURE', detail: msg }] : []
}

function issuesAt(issues: Issue[], field: string): Issue[] {
  return issues.filter((i) => i.field === field)
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

function FieldIssues({ issues, id }: { issues: Issue[]; id: string }): JSX.Element | null {
  if (issues.length === 0) return null
  return (
    <div className="field-issue" id={id} role="alert">
      {issues.map((i) => (
        <p key={i.code + i.detail} className="field-issue-line">
          <span className="field-issue-code">{i.code.replace(/_/g, ' ').toLowerCase()}</span> {i.detail}
        </p>
      ))}
    </div>
  )
}

// FORBIDDEN is the capability wall; any other 403 here is the ownership rule
// refusing somebody who holds the act for a vendor that is not theirs, and the
// API's own message names who to ask.
function Refusal({ error, onDismiss }: { error: ApiError; onDismiss: () => void }): JSX.Element {
  let heading = 'That did not go through'
  let body = error.message
  let next = error.remedy

  if (error.status === 403 && error.code === 'FORBIDDEN') {
    heading = 'Your role cannot change the sub-processor register'
    body = 'Reading a vendor and naming the parties behind it are separate permissions'
    next = refusalReason('vendor.edit')
  } else if (error.status === 403) {
    heading = 'This vendor is not yours to change'
  } else if (error.status === 422 && error.issues.length > 0) {
    heading = 'Refused'
    body = 'The issues are marked against the fields below'
  }

  return (
    <div className="note stop decision-note" role="alert">
      <strong>{heading}</strong>
      <p>
        {body} <ReasonTip label="What to do next" text={next} />
      </p>
      {/* The code and the reference support looks the request up by
          (release 3.1); shown here, so the feedback net adds no toast. */}
      <ErrorReferenceOf error={error} />
      <button type="button" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  )
}

export function VendorSubprocessors({
  vendorId,
  onChanged,
}: {
  vendorId: string
  onChanged?: () => void
}): JSX.Element {
  const [rows, setRows] = useState<Subprocessor[] | null>(null)
  const [counts, setCounts] = useState<{ active: number; outsideIndia: number } | null>(null)
  const [error, setError] = useState<unknown>(null)

  const [editing, setEditing] = useState<Editing | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [issues, setIssues] = useState<Issue[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)

  const [withdrawing, setWithdrawing] = useState<Subprocessor | null>(null)
  const [withdrawReason, setWithdrawReason] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [outcome, setOutcome] = useState('')
  const [rowError, setRowError] = useState<ApiError | null>(null)

  const load = useCallback(() => {
    api
      .subprocessors(vendorId)
      .then((r) => {
        const list = r.subprocessors ?? []
        setRows(list)
        // The API's counts, not a second reading of the same rows. It decides
        // what counts as active and as outside India for the tier rule and the
        // residency view, and a stat strip that disagreed with the panel above
        // it would be worse than no stat strip.
        setCounts({
          active: r.active ?? list.filter((s) => s.status !== 'WITHDRAWN').length,
          outsideIndia: r.outsideIndia ?? list.filter((s) => s.status !== 'WITHDRAWN' && outsideIndia(s.country)).length,
        })
        setError(null)
      })
      .catch(setError)
  }, [vendorId])

  useEffect(load, [load])

  if (error != null) return <ErrorBox error={error} what="The sub-processors behind this vendor" />
  if (!rows || !counts) return <Loading what="the sub-processors" />

  const canEdit = can('vendor.edit')
  const withdrawn = rows.filter((r) => r.status === 'WITHDRAWN').length

  function beginNew() {
    setEditing({ mode: 'new' })
    setDraft(EMPTY_DRAFT)
    setIssues([])
    setSaveError(null)
    setOutcome('')
    setRowError(null)
    setWithdrawing(null)
  }

  function beginEdit(row: Subprocessor) {
    setEditing({ mode: 'edit', row })
    setDraft({
      legalName: row.legalName,
      country: row.country ?? '',
      serviceDescription: row.serviceDescription ?? '',
      declaredBy: row.declaredBy || 'VENDOR',
      firstSeenOn: (row.firstSeenOn ?? '').slice(0, 10),
      status: row.status || 'DECLARED',
    })
    setIssues([])
    setSaveError(null)
    setOutcome('')
    setRowError(null)
    setWithdrawing(null)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!editing) return
    const found = validate(draft, editing.mode === 'new', editing.mode === 'edit' ? (editing.row.firstSeenOn ?? '').slice(0, 10) : '')
    setIssues(found)
    if (found.length > 0) return

    const body: SubprocessorWrite = {
      legalName: editing.mode === 'new' ? draft.legalName.trim() : editing.row.legalName,
      country: draft.country.trim().toUpperCase(),
      serviceDescription: draft.serviceDescription.trim(),
      declaredBy: draft.declaredBy,
      firstSeenOn: draft.firstSeenOn,
      status: draft.status,
    }
    // Carried through rather than dropped. Nothing on this screen sets the
    // source document, and the update route takes what it is sent, so leaving
    // it out would clear one recorded by an import.
    if (editing.mode === 'edit' && editing.row.sourceDocumentId) {
      body.sourceDocumentId = editing.row.sourceDocumentId
    }

    setSaving(true)
    setSaveError(null)
    const call =
      editing.mode === 'new'
        ? api.createSubprocessor(vendorId, body).then((r) => {
            const shared = r.alsoBehindVendorIds ?? []
            const said = 'Added ' + body.legalName + ' to the register.'
            if (shared.length === 0) return said
            return (
              said +
              ' It is already named behind ' +
              shared.length +
              ' other vendor' +
              (shared.length === 1 ? '' : 's') +
              ' here. ' +
              (r.note ?? '')
            )
          })
        : api
            .updateSubprocessor(vendorId, editing.row.id, body)
            .then(
              () =>
                'Saved. ' +
                body.legalName +
                ' reads as ' +
                (STATUS_LABEL[body.status] ?? body.status).toLowerCase() +
                '.',
            )

    call
      .then((said) => {
        setEditing(null)
        setDraft(EMPTY_DRAFT)
        setOutcome(said)
        load()
        if (onChanged) onChanged()
      })
      .catch((err) => {
        const failed = asApiError(err)
        setSaveError(failed)
        if (failed.issues.length > 0) setIssues(failed.issues)
      })
      .finally(() => setSaving(false))
  }

  function withdraw(row: Subprocessor, reason: string) {
    setBusyId(row.id)
    setOutcome('')
    setRowError(null)
    api
      .withdrawSubprocessor(vendorId, row.id, reason)
      .then(() => {
        setWithdrawing(null)
        setWithdrawReason('')
        // One short line. Why a withdrawn party stays in the list is the
        // system's reasoning, and the review of 17 September asked for it off
        // the screen.
        setOutcome('Withdrawn. Kept in the list for past incidents.')
        load()
        if (onChanged) onChanged()
      })
      .catch((err) => setRowError(asApiError(err)))
      .finally(() => setBusyId(null))
  }

  const columns: Column<Subprocessor>[] = [
    {
      key: 'party',
      header: 'Party',
      sortValue: (r) => r.legalName,
      render: (r) => (
        <>
          <span className="cell-primary">{r.legalName}</span>
          <span className="cell-sub">{r.serviceDescription || 'what they do is not recorded'}</span>
        </>
      ),
    },
    {
      key: 'country',
      header: 'Country',
      sortValue: (r) => r.country ?? null,
      render: (r) =>
        r.country ? (
          <>
            <span className="mono">{r.country}</span>
            {outsideIndia(r.country) && <span className="cell-sub">outside India</span>}
          </>
        ) : (
          <span className="muted">not recorded</span>
        ),
    },
    {
      key: 'declaredBy',
      header: 'On whose word',
      sortValue: (r) => r.declaredBy,
      render: (r) => (r.declaredBy === 'TENANT' ? 'We found it' : 'The vendor'),
    },
    {
      key: 'firstSeen',
      header: 'First seen',
      sortValue: (r) => r.firstSeenOn ?? null,
      render: (r) =>
        r.firstSeenOn ? <span className="mono">{shortDate(r.firstSeenOn)}</span> : <span className="muted">no date</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => r.status,
      render: (r) => (
        <Status tone={STATUS_TONE[r.status] ?? 'neutral'}>{STATUS_LABEL[r.status] ?? r.status}</Status>
      ),
    },
    {
      key: 'actions',
      header: 'Change',
      render: (r) => (
        <span className="exc-actions">
          <button
            type="button"
            onClick={() => beginEdit(r)}
            disabled={!canEdit}
            title={canEdit ? undefined : refusalReason('vendor.edit')}
          >
            Edit
          </button>
          <button
            type="button"
            disabled={!canEdit || busyId === r.id || r.status === 'WITHDRAWN'}
            aria-expanded={withdrawing?.id === r.id}
            aria-controls="sp-withdraw"
            title={
              r.status === 'WITHDRAWN'
                ? 'Already withdrawn. Edit the row to put them back in the chain.'
                : canEdit
                  ? undefined
                  : refusalReason('vendor.edit')
            }
            onClick={() => {
              setWithdrawing(withdrawing?.id === r.id ? null : r)
              setWithdrawReason('')
              setOutcome('')
              setRowError(null)
            }}
          >
            Withdraw
          </button>
        </span>
      ),
    },
  ]

  const nameIssues = issuesAt(issues, 'legalName')
  const countryIssues = issuesAt(issues, 'country')
  const serviceIssues = issuesAt(issues, 'serviceDescription')
  const seenIssues = issuesAt(issues, 'firstSeenOn')
  const chosenStatus = STATUSES.find((s) => s.value === draft.status)

  return (
    <section className="panel panel-flush" aria-labelledby="sp-heading">
      <div className="panel-head">
        <h2 id="sp-heading" style={{ margin: 0 }}>
          Sub-processors
        </h2>
        <p className="summary-line sw-row" style={{ margin: 0 }} role="status">
          <span>
            <strong>{counts.active}</strong> in the chain today
          </span>
        </p>
      </div>

      <div className="stat-strip">
        <div className="stat">
          <b>{counts.active}</b>
          <span>named parties</span>
        </div>
        <div className={counts.outsideIndia > 0 ? 'stat alert' : 'stat'}>
          <b>{counts.outsideIndia}</b>
          <span>outside India</span>
        </div>
        <div className="stat">
          <b>{withdrawn}</b>
          <span>withdrawn</span>
        </div>
      </div>

      <div className="contact-block">
        {outcome && (
          <p className="ok-note" role="status">
            {outcome}
          </p>
        )}
        {rowError !== null && <Refusal error={rowError} onDismiss={() => setRowError(null)} />}

      </div>

      {withdrawing !== null && (
        <div className="contact-block">
          <form
            className="reject-form"
            id="sp-withdraw"
            onSubmit={(e) => {
              e.preventDefault()
              withdraw(withdrawing, withdrawReason.trim())
            }}
          >
            <label htmlFor="sp-withdraw-reason">Why {withdrawing.legalName} is out of the chain</label>
            <div className="reject-row">
              <input
                id="sp-withdraw-reason"
                type="text"
                value={withdrawReason}
                aria-describedby="sp-withdraw-help"
                onChange={(e) => setWithdrawReason(e.target.value)}
              />
              <button
                type="submit"
                disabled={busyId === withdrawing.id}
                data-action="tprm.vendor,vendor-record.a28"
              >
                {busyId === withdrawing.id ? 'Withdrawing' : 'Withdraw this party'}
              </button>
              <button type="button" onClick={() => setWithdrawing(null)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <DataTable
        caption="Sub-processors named behind this vendor, withdrawn ones included."
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        defaultSort={{ key: 'party', dir: 'asc' }}
        empty={{
          title: 'No sub-processor named for this vendor',
          detail: (
            <>
              Nobody sits behind it, or nobody has asked{' '}
              <Toggletip label="Why an empty list is not an answer">
                The two look identical from here, and only one of them is safe.
              </Toggletip>
            </>
          ),
        }}
      />

      <div className="section-head">
        {editing !== null && editing.mode === 'edit' ? 'Edit this party' : 'Add a party'}
      </div>

      <div className="contact-block">
        {editing === null ? (
          <button
            type="button"
            className="primary"
            onClick={beginNew}
            disabled={!canEdit}
            title={canEdit ? undefined : refusalReason('vendor.edit')}
          >
            Add a sub-processor
          </button>
        ) : (
          <form className="fieldset" onSubmit={submit} noValidate>
            {saveError !== null && <Refusal error={saveError} onDismiss={() => setSaveError(null)} />}

            <div className="form-grid">
              <div className="field">
                <label htmlFor="sp-name">Legal name</label>
                <input
                  id="sp-name"
                  type="text"
                  value={draft.legalName}
                  disabled={editing.mode === 'edit'}
                  aria-invalid={nameIssues.length > 0 ? true : undefined}
                  aria-describedby={'sp-name-why' + (nameIssues.length > 0 ? ' sp-name-issue' : '')}
                  onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
                />
                {editing.mode === 'edit' ? (
                  <p className="field-locked" id="sp-name-why">
                    Locked, the register is keyed on this name{' '}
                    <Toggletip label="How to record a new name">
                      A party that changed name is withdrawn and recorded again, so the chain as it stood before the
                      change stays readable.
                    </Toggletip>
                  </p>
                ) : null}
                <FieldIssues issues={nameIssues} id="sp-name-issue" />
              </div>

              <div className="field">
                <label htmlFor="sp-country">Country</label>
                <input
                  id="sp-country"
                  type="text"
                  value={draft.country}
                  placeholder="IN"
                  maxLength={2}
                  aria-invalid={countryIssues.length > 0 ? true : undefined}
                  aria-describedby={'sp-country-why' + (countryIssues.length > 0 ? ' sp-country-issue' : '')}
                  onChange={(e) => setDraft({ ...draft, country: e.target.value.toUpperCase() })}
                />
                <FieldIssues issues={countryIssues} id="sp-country-issue" />
              </div>

              <div className="field field-wide">
                <label htmlFor="sp-service">What they do</label>
                <input
                  id="sp-service"
                  type="text"
                  value={draft.serviceDescription}
                  aria-invalid={serviceIssues.length > 0 ? true : undefined}
                  aria-describedby={'sp-service-why' + (serviceIssues.length > 0 ? ' sp-service-issue' : '')}
                  onChange={(e) => setDraft({ ...draft, serviceDescription: e.target.value })}
                />
                <FieldIssues issues={serviceIssues} id="sp-service-issue" />
              </div>

              <div className="field">
                <label htmlFor="sp-declared">How we know</label>
                <select
                  id="sp-declared"
                  value={draft.declaredBy}
                  aria-describedby="sp-declared-why"
                  onChange={(e) => setDraft({ ...draft, declaredBy: e.target.value })}
                >
                  {DECLARED_BY.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="sp-seen">First seen on</label>
                <input
                  id="sp-seen"
                  type="date"
                  value={draft.firstSeenOn}
                  aria-invalid={seenIssues.length > 0 ? true : undefined}
                  aria-describedby={seenIssues.length > 0 ? 'sp-seen-issue' : undefined}
                  onChange={(e) => setDraft({ ...draft, firstSeenOn: e.target.value })}
                  onBlur={() => setIssues((cur) => [...cur.filter((i) => i.field !== 'firstSeenOn'), ...firstSeenIssues(draft, editing?.mode === 'edit' ? (editing.row.firstSeenOn ?? '').slice(0, 10) : '')])}
                />
                <FieldIssues issues={seenIssues} id="sp-seen-issue" />
              </div>

              <div className="field">
                <label htmlFor="sp-status">Status</label>
                <select
                  id="sp-status"
                  value={draft.status}
                  aria-describedby="sp-status-why"
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <p className="field-note" id="sp-status-why">
                  {chosenStatus?.detail ?? 'Declared until somebody checks it'}{' '}
                  {chosenStatus?.why && <Toggletip label={'About ' + chosenStatus.label}>{chosenStatus.why}</Toggletip>}
                </p>
              </div>
            </div>

            <div className="commit-bar">
              <button
                type="submit"
                className="primary"
                disabled={saving}
                data-action="tprm.vendor,vendor-record.a26"
                data-action-also="tprm.vendor,vendor-record.a27"
              >
                {saving ? 'Saving' : editing.mode === 'new' ? 'Add this party' : 'Save changes'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(null)
                  setIssues([])
                  setSaveError(null)
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  )
}
